import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    type UpdateMembershipGroupMemberFormSchemaValues,
    updateMembershipGroupMemberFormSchema,
    useUpdateMembershipGroupMember,
} from 'src/features/memberships/api/useUpdateMembershipGroupMember';
import { accessLevelOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    DialogHeader,
    DialogSeparator,
    DialogBody,
    DialogFooter,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormField, FormItem, FormLabel, FormControlStack, FormControl, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { MembershipGroupMember } from 'src/features/memberships/types';

interface MembershipGroupMemberEditFormModalProps {
    onSuccess: (values: UpdateMembershipGroupMemberFormSchemaValues) => void;
    parkingId: string;
    membershipGroupId: number;
    membershipGroupMember: MembershipGroupMember | null;
    isMemberModalOpen: boolean;
    setIsMemberModalOpen: (isOpen: boolean) => void;
}
const MembershipGroupMemberEditFormModal = ({
    onSuccess,
    parkingId,
    membershipGroupId,
    membershipGroupMember,
    isMemberModalOpen,
    setIsMemberModalOpen,
}: MembershipGroupMemberEditFormModalProps) => {
    const { formatMessage } = useIntl();

    const form = useForm<UpdateMembershipGroupMemberFormSchemaValues>({
        resolver: zodResolver(updateMembershipGroupMemberFormSchema),
        defaultValues: {
            access_level: membershipGroupMember?.access_level,
        },
    });

    React.useEffect(() => {
        if (membershipGroupMember) {
            form.reset({
                access_level: membershipGroupMember.access_level,
            });
        }
    }, [membershipGroupMember]);

    const { mutate: updateMembershipMember, isPending: isPendingUpdate } = useUpdateMembershipGroupMember({
        onSuccess: () => {
            if (onSuccess) {
                onSuccess(form.getValues());
            }
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.editMembershipGroupMemberSuccess),
                description: formatMessage(membershipMessages.editMembershipGroupMemberSuccessDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleSubmit = (values: UpdateMembershipGroupMemberFormSchemaValues) => {
        const submitData = {
            membershipGroupId,
            parkingId,
            membershipMemberId: membershipGroupMember?.id ?? '',
            ...values,
        };

        updateMembershipMember(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        if (setIsMemberModalOpen) setIsMemberModalOpen(isOpen);
    };

    return (
        <Dialog open={isMemberModalOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        <FormattedMessage {...membershipMessages.editMember} />
                    </DialogTitle>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(handleSubmit)} variant="dialog">
                        <FormField
                            control={form.control}
                            name="access_level"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog">
                                        <FormattedMessage {...membershipMessages.accessLevel} />
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(
                                                            membershipMessages.accessLevelPlaceholder,
                                                        )}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {accessLevelOptions.map((role) => (
                                                        <SelectItem key={role.value} value={role.value}>
                                                            {formatMessage(role.label)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage>
                                            <FormattedMessage
                                                {...membershipMessages.accessLevelTip}
                                                values={{
                                                    b: (chunks: React.ReactNode[]) => (
                                                        <b className="font-semibold">{chunks}</b>
                                                    ),
                                                    p: (chunks: React.ReactNode[]) => (
                                                        <span>
                                                            {chunks}
                                                            <br />
                                                        </span>
                                                    ),
                                                }}
                                            />
                                        </FormMessage>
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPendingUpdate || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(handleSubmit)}
                        className="flex-1"
                    >
                        {isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...generalMessages.saveChanges} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { MembershipGroupMemberEditFormModal };
